import { EditorAPI } from './EditorAPI';
import { Modification } from '@core/types';
import { CampaignError } from '@shared/errors';
import { ERROR_MESSAGES, FATAL_ERRORS } from '@shared/constants';

export class SessionManager {
  private api: EditorAPI;
  private token: string;
  private pageUrl: string;
  private isVerified = false;
  private sessionData: any = null;

  constructor(api: EditorAPI, token: string, pageUrl: string) {
    this.api = api;
    this.token = token;
    this.pageUrl = pageUrl;
  }

  async verify(): Promise<any> {
    try {
      const response = await this.api.verifySession(this.token, this.pageUrl);

      if (!response.ok) {
        const reason = response.reason || 'unknown';
        const message =
          ERROR_MESSAGES[reason as keyof typeof ERROR_MESSAGES] || ERROR_MESSAGES.unknown;

        if (FATAL_ERRORS.some((fatal) => fatal === reason)) {
          throw new CampaignError(message, reason);
        }

        throw new CampaignError(message, reason);
      }

      this.isVerified = true;
      this.sessionData = response.data;
      return this.sessionData;
    } catch (error) {
      if (error instanceof CampaignError) {
        throw error;
      }
      throw new CampaignError('Session verification failed', 'verification_failed', error);
    }
  }

  async loadModifications(): Promise<Modification[]> {
    if (!this.isVerified) {
      throw new CampaignError('Session not verified', 'not_verified');
    }

    try {
      const response = await this.api.loadModifications(this.token, this.pageUrl);

      if (!response.ok) {
        const reason = response.reason || 'unknown';
        const message =
          ERROR_MESSAGES[reason as keyof typeof ERROR_MESSAGES] || ERROR_MESSAGES.unknown;
        throw new CampaignError(message, reason);
      }

      const modifications = Array.isArray(response.data?.modifications)
        ? response.data.modifications
        : [];

      return modifications
        .sort((a: any, b: any) => (a.order ?? 0) - (b.order ?? 0))
        .map((mod: any) => ({
          id: mod.id,
          selector: mod.selector,
          action: mod.action,
          value: mod.value ?? null,
          attr_name: mod.attr_name ?? null,
          media_temp_token: mod.media_temp_token ?? undefined,
        }));
    } catch (error) {
      if (error instanceof CampaignError) {
        throw error;
      }
      throw new CampaignError('Failed to load modifications', 'load_failed', error);
    }
  }

  async saveModifications(modifications: Modification[]): Promise<void> {
    if (!this.isVerified) {
      throw new CampaignError('Session not verified', 'not_verified');
    }

    try {
      const response = await this.api.saveModifications(this.token, this.pageUrl, modifications);

      if (!response.ok) {
        const reason = response.reason || 'unknown';
        const message =
          ERROR_MESSAGES[reason as keyof typeof ERROR_MESSAGES] || ERROR_MESSAGES.unknown;
        throw new CampaignError(message, reason);
      }
    } catch (error) {
      if (error instanceof CampaignError) {
        throw error;
      }
      throw new CampaignError('Failed to save modifications', 'save_failed', error);
    }
  }

  isSessionVerified(): boolean {
    return this.isVerified;
  }

  getSessionData(): any {
    return this.sessionData;
  }

  getToken(): string {
    return this.token;
  }

  getPageUrl(): string {
    return this.pageUrl;
  }
}
